/**
 * Google Ads tag — one loader, one ID.
 *
 * The tag used to be installed twice (App.js and components/script.js), both
 * hardcoded to the same conversion ID. Two loaders means two chances to drift
 * apart, which is exactly what happened when a new Ads account arrived. The ID
 * now lives in .env so switching accounts is a config change, not a code hunt.
 *
 *   REACT_APP_GOOGLE_ADS_ID               e.g. AW-18344143316
 *   REACT_APP_GOOGLE_ADS_CONVERSION_LABEL  the per-action label from Ads
 */
const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "AW-18344143316"
const CONVERSION_LABEL = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL || ""

function ensureGtag() {
  window.dataLayer = window.dataLayer || []
  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments)
    }
  }
  return window.gtag
}

/** Idempotent: safe to call from anywhere, loads the script at most once. */
export function loadGoogleAds() {
  try {
    if (!ADS_ID) return
    const gtag = ensureGtag()
    if (document.getElementById("gtag-aw")) return

    const s = document.createElement("script")
    s.id = "gtag-aw"
    s.async = true
    s.src = `https://www.googletagmanager.com/gtag/js?id=${ADS_ID}`
    s.onerror = () => console.warn("Google Ads tag failed to load (ignored).")
    document.head.appendChild(s)

    gtag("js", new Date())
    gtag("config", ADS_ID)
  } catch (e) {
    console.warn("Google Ads init error (ignored):", e)
  }
}

/**
 * Report a booking to Google Ads.
 *
 * Refuses to fire without a conversion label rather than sending
 * `send_to: "AW-18344143316/"`, which Ads silently discards — a conversion that
 * looks sent but never arrives is the worst of both worlds. Real booked value
 * only; never a quoted or estimated one.
 */
export function trackBookingConversion({ value, currency = "INR", transactionId } = {}) {
  try {
    if (!ADS_ID) return false
    if (!CONVERSION_LABEL) {
      console.warn(
        "[ads] conversion NOT sent — REACT_APP_GOOGLE_ADS_CONVERSION_LABEL is not set. " +
          "Copy the label from Google Ads › Goals › the conversion action (the part after the slash in send_to)."
      )
      return false
    }
    const amount = Number(value)
    if (!Number.isFinite(amount) || amount <= 0) return false

    ensureGtag()("event", "conversion", {
      send_to: `${ADS_ID}/${CONVERSION_LABEL}`,
      value: amount,
      currency,
      ...(transactionId ? { transaction_id: String(transactionId) } : {}), // de-dupes on reload
    })
    return true
  } catch (e) {
    console.warn("[ads] conversion error (ignored):", e)
    return false
  }
}

export default { loadGoogleAds, trackBookingConversion }
